/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-get */
import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import config from '@linc-technologies/linced-common/utils/config';
import {
  getWebInstrumentations,
  initializeFaro,
  TransportItemType,
} from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';

// URLs that `traceparent` is attached to. Every app talks to an API on a
// different origin (api4/dev-api/staging-api.linc-ed.com), and OpenTelemetry's
// browser fetch/XHR instrumentation refuses to propagate trace headers
// cross-origin unless the URL matches this list. Without it nothing is sent
// and the whole feature is silently inert.
//
// The negative lookahead deliberately excludes /media/ paths. Those endpoints
// 302-redirect to imagedelivery.net (a CDN) which does not allow `traceparent`
// in its CORS preflight, so propagating there fails the request and breaks
// avatar loading. Do not simplify this to /linc-ed\.com/i.
const TRACE_PROPAGATION_TARGETS = [/linc-ed\.com\/(?!media\/)/i];

// Faro's own ingest endpoint is not application traffic, and instrumenting it
// risks a feedback loop. The fetch transport already ignores its exact URL;
// this widens that to the whole host.
const IGNORE_URLS = [/alloy\.linc-ed\.com/i];

export default class FaroService extends Service {
  @tracked
  faro = null;
  @tracked
  appName = null;

  init() {
    super.init(...arguments);

    // Whether Faro runs at all is a config flag, not a hardcoded environment
    // check: see `faro.enabled` in each app's config/environment.js. Absent
    // config means enabled, so an app that has not opted out still reports.
    if (config.faro && config.faro.enabled === false) {
      return;
    }

    const defaultName =
      (config.faro && config.faro.appName) || config.modulePrefix;
    this.appName = defaultName;

    // Faro registers a global singleton and patches console (captureConsole).
    // Tests rebuild the container every module, so initializeFaro() would run
    // repeatedly, emitting "Faro is already registered" and adding enough
    // console-capture overhead to time tests out. Skip real init under test;
    // `faro` stays null and the push* methods below no-op safely.
    if (config.environment === 'test') {
      return;
    }

    const faro = initializeFaro(
      Object.assign({
        url: 'https://alloy.linc-ed.com/collect',
        instrumentations: [
          ...getWebInstrumentations({ captureConsole: true }),
          new TracingInstrumentation({
            instrumentationOptions: {
              propagateTraceHeaderCorsUrls: TRACE_PROPAGATION_TARGETS,
            },
          }),
        ],
        ignoreUrls: IGNORE_URLS,
        // Spans exist purely to mint a trace id and put it on the wire as a
        // W3C `traceparent` header, so backend log lines can be joined to
        // frontend Faro logs. We have no trace backend, so no span data may
        // leave the browser: this drops every TRACE item at the transport
        // boundary. Returning null from beforeSend discards the item.
        //
        // This does NOT cost us log correlation. LOG, EXCEPTION, EVENT and
        // MEASUREMENT items carry the trace context in their own `trace`
        // field and are passed through untouched, so Faro logs still reach
        // Alloy and now carry a trace_id.
        //
        // Deleting this hook is exactly how traces get switched on once there
        // is a collector to receive them. It is not dead code.
        beforeSend: (item) =>
          item.type === TransportItemType.TRACE ? null : item,
        app: {
          name: this.get('appName'),
          version: config.appVersion,
          environment: config.appEnvironment,
        },
      }),
    );
    this.faro = faro;
    this.pushEvent('ember:boot');
  }

  pushError(value, options = {}) {
    if (!this.faro) {
      return;
    }
    this.faro.api.pushError(value, options);
  }

  pushLog(value, options = {}) {
    if (!this.faro) {
      return;
    }
    this.faro.api.pushLog([value], options);
  }

  pushEvent(name, attributes, domain, options) {
    if (!this.faro) {
      return;
    }
    this.faro.api.pushEvent(name, attributes, domain, options);
  }
}
